<script setup lang="ts">
import DropdownItem from '#components/elements/dropdown/DropdownItem.vue';
import DropdownMenu from '#components/elements/dropdown/DropdownMenu.vue';
import PopOver from '#components/popover/PopOver.vue';
import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import { usePermissions } from '#composables/usePermissions';
import type { StorePerimeterType } from '@carrefour-gcs/shared';
import { computed } from 'vue';

/**
 * @deprecated Legacy store perimeter selector, the like for like restrictions are driven by the database in the
 * new filters (FilterDefinitionValueRestriction).
 * TODO: [FILTER] Remove this once the new filters are done.
 */
const { t } = useLanguage();

const {
  taxonomy: { taxonomy },
  storePerimeter: { storePerimeter, updateStorePerimeter }
} = useKpisPerimeter();
const { hasAccessToStorePerimeter, hasAccessToLikeForLike } = usePermissions();

const changeLikeForLike = async (value: StorePerimeterType) => {
  await updateStorePerimeter(value);
};

const dropdownDisabledInfoMessage = computed(() => {
  if (taxonomy.value === 'nielsen') {
    return t('performanceCarrefourCatman.storePerimeter.nielsenAllStores');
  }

  return undefined;
});
</script>

<template>
  <DropdownMenu
    v-if="hasAccessToStorePerimeter"
    :no-padding="true"
    background="outline"
    variant="default"
    :disabled="taxonomy === 'nielsen'"
    :popover="dropdownDisabledInfoMessage"
  >
    <span class="text-default-disabled">
      {{ t('commonLabel.storePerimeter') }}
    </span>
    <span class="text-black">
      {{
        storePerimeter === 'likeForLike'
          ? t('performanceCarrefourCatman.storePerimeter.likeForLike')
          : t('performanceCarrefourCatman.storePerimeter.allStores')
      }}
    </span>

    <template #items>
      <PopOver
        v-for="item in [
          {
            value: 'allStores' as const,
            label: t('performanceCarrefourCatman.storePerimeter.allStores')
          },
          {
            value: 'likeForLike' as const,
            label: t('performanceCarrefourCatman.storePerimeter.likeForLike')
          }
        ]"
        :key="`${item.value}.likeForLike`"
        class-name="w-full"
      >
        <DropdownItem
          :disabled="!hasAccessToLikeForLike && item.value === 'likeForLike'"
          @click="changeLikeForLike(item.value)"
        >
          <button v-if="storePerimeter !== item.value">
            {{ item.label }}
          </button>
          <strong v-else>{{ item.label }} </strong>

          <VueFeather
            v-if="!hasAccessToLikeForLike && item.value === 'likeForLike'"
            type="info"
            size="12"
            class="ml-2"
          />
        </DropdownItem>

        <template v-if="!hasAccessToLikeForLike && item.value === 'likeForLike'" #content
          ><span
            >{{ t('performanceCarrefourCatman.storePerimeter.hintLikeForLikeNotAvailableWithEcommerce') }}
          </span></template
        >
      </PopOver>
    </template>
  </DropdownMenu>
</template>
